import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Collapse, Tooltip } from '@mui/material';
import CustomSnackBar from 'components/CustomSnackBar';
import LoadingDialog from 'components/global/LoadingDialog';
import * as LucideIcons from 'lucide-react';

import { useSelector, useDispatch } from 'react-redux';
import { logout } from 'context/actions/auth';
import { SET_ALERT } from 'context/actions/system';

import schoolLogo from 'public/logo.png';

import { ChevronDown, ChevronRight, LogOut, Menu as MenuIcon } from 'lucide-react';

// ── Sidebar хэмжээ ──────────────────────────────────────────────────
const SIDEBAR_W = 260;
const SIDEBAR_W_C = 64;

// ── Өнгөний тохиргоо ────────────────────────────────────────────────
// Бараан navy, гэхдээ амилсан байдалтай (хэт хар биш)
const BG_TOP = 'hsl(220 28% 18%)'; // sidebar-ийн дээд хэсэг
const BG_BOTTOM = 'hsl(220 26% 14%)'; // sidebar-ийн доод хэсэг
const BORDER = 'rgba(255,255,255,0.1)';
const TEXT_ACTIVE = '#fff';
const TEXT_INACTIVE = 'rgba(255,255,255,0.78)';
const TEXT_ICON_INACTIVE = 'rgba(255,255,255,0.62)';
const TEXT_MUTED = 'rgba(255,255,255,0.42)';
const BG_ACTIVE = 'rgba(255,255,255,0.1)';
const BG_HOVER = 'rgba(255,255,255,0.05)';
const ACCENT_YELLOW = 'hsl(45 100% 60%)';
const DANGER = 'rgba(255,120,120,0.85)';
const DANGER_HOVER_BG = 'rgba(220,60,60,0.14)';

const AdminLayout = ({ children }) => {
    const router = useRouter();
    const auth = useSelector((s) => s.auth);
    const system = useSelector((s) => s.system);
    const dispatch = useDispatch();

    const [collapsed, setCollapsed] = useState(false);
    const [menus, setMenus] = useState([]);
    const [openMenus, setOpenMenus] = useState({});
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) return;
        if (!auth.loggedIn) {
            router.push('/admin');
            return;
        }
        setMenus(auth.user?.menus ?? []);
    }, [auth, mounted]);

    const isActive = (url) => router.pathname === `/admin/${url}`;
    const handleLogout = () => logout(dispatch);

    // ═══════════════════════════════════════════════════════════════
    // MenuItem компонент
    // ═══════════════════════════════════════════════════════════════
    const MenuItem = ({ menu, depth = 0 }) => {
        const active = isActive(menu.url);
        const hasChildren = menu.children?.length > 0;
        const open = openMenus[menu.id] ?? false;

        const btn = (
            <button
                onClick={() => {
                    if (hasChildren) setOpenMenus((p) => ({ ...p, [menu.id]: !open }));
                    else router.push('/admin/' + menu.url);
                }}
                style={{
                    all: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: collapsed ? '9px 0' : `8px ${10 + depth * 14}px`,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    borderRadius: 8,
                    cursor: 'pointer',
                    position: 'relative',
                    marginBottom: 2,
                    background: active ? BG_ACTIVE : 'transparent',
                    transition: 'background .15s',
                }}
                onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = BG_HOVER;
                }}
                onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                }}
            >
                {active && (
                    <span
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: '22%',
                            height: '56%',
                            width: 3,
                            borderRadius: '0 3px 3px 0',
                            background: ACCENT_YELLOW,
                        }}
                    />
                )}

                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                    }}
                >
                    {menu.icon && LucideIcons[menu.icon] ? (
                        React.createElement(LucideIcons[menu.icon], {
                            size: depth > 0 ? 13 : 16,
                            strokeWidth: active ? 2.2 : 1.8,
                            style: {
                                color: active ? ACCENT_YELLOW : TEXT_ICON_INACTIVE,
                                transition: 'color .15s',
                            },
                        })
                    ) : (
                        <span
                            style={{
                                width: depth > 0 ? 4 : 6,
                                height: depth > 0 ? 4 : 6,
                                borderRadius: '50%',
                                background: active ? ACCENT_YELLOW : 'rgba(255,255,255,0.3)',
                                transition: 'background .15s',
                            }}
                        />
                    )}
                </span>

                {!collapsed && (
                    <>
                        <span
                            style={{
                                flex: 1,
                                fontSize: depth > 0 ? 12.5 : 13.5,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: active ? 600 : 500,
                                letterSpacing: '-0.005em',
                                color: active ? TEXT_ACTIVE : TEXT_INACTIVE,
                                transition: 'color .15s',
                            }}
                        >
                            {menu.name}
                        </span>
                        {hasChildren && (
                            <span style={{ color: TEXT_MUTED, display: 'flex' }}>
                                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </span>
                        )}
                    </>
                )}
            </button>
        );

        return (
            <>
                {collapsed ? (
                    <Tooltip title={menu.name} placement="right" arrow>
                        <span style={{ display: 'block' }}>{btn}</span>
                    </Tooltip>
                ) : (
                    btn
                )}
                {hasChildren && !collapsed && (
                    <Collapse in={open} timeout={180} unmountOnExit>
                        <div style={{ marginTop: 1, marginBottom: 4 }}>
                            {menu.children.map((c, i) => (
                                <MenuItem key={i} menu={c} depth={depth + 1} />
                            ))}
                        </div>
                    </Collapse>
                )}
            </>
        );
    };

    // ═══════════════════════════════════════════════════════════════
    // SSR / нэвтрээгүй үе
    // ═══════════════════════════════════════════════════════════════
    if (!mounted || !auth.loggedIn) {
        return <div style={{ minHeight: '100vh', background: '#fff' }} />;
    }

    // ═══════════════════════════════════════════════════════════════
    // Main layout
    // ═══════════════════════════════════════════════════════════════
    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#F9FAFB' }}>
            {/* ══════════ SIDEBAR ══════════ */}
            <aside
                style={{
                    width: collapsed ? SIDEBAR_W_C : SIDEBAR_W,
                    minHeight: '100vh',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transition: 'width .24s cubic-bezier(.4,0,.2,1)',
                    background: `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOTTOM} 100%)`,
                    boxShadow: '4px 0 28px rgba(0,0,0,0.18)',
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                }}
            >
                {/* ── Header: лого + toggle ── */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'space-between',
                        padding: collapsed ? '16px 0' : '16px 16px',
                        borderBottom: `1px solid ${BORDER}`,
                        marginBottom: 8,
                        flexShrink: 0,
                        minHeight: 64,
                        boxSizing: 'border-box',
                    }}
                >
                    {!collapsed && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    background: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Image
                                    src={schoolLogo}
                                    width={36}
                                    height={36}
                                    alt="Хүрээ Тулга"
                                    style={{ objectFit: 'contain', padding: 2 }}
                                />
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        color: '#fff',
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Хүрээ Тулга
                                </div>
                                <div
                                    style={{
                                        fontSize: 10,
                                        color: TEXT_MUTED,
                                        letterSpacing: '.08em',
                                        textTransform: 'uppercase',
                                        marginTop: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    Admin Panel
                                </div>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => setCollapsed((v) => !v)}
                        style={{
                            all: 'unset',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            borderRadius: 6,
                            background: 'rgba(255,255,255,0.07)',
                            color: TEXT_ICON_INACTIVE,
                            flexShrink: 0,
                            transition: 'background .15s, color .15s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.14)';
                            e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                            e.currentTarget.style.color = TEXT_ICON_INACTIVE;
                        }}
                        aria-label={collapsed ? 'Нээх' : 'Хаах'}
                    >
                        {collapsed ? <ChevronRight size={16} /> : <MenuIcon size={16} />}
                    </button>
                </div>

                {/* ── Цэс ── */}
                <nav
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingBottom: 12,
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255,255,255,0.1) transparent',
                    }}
                >
                    <div style={{ padding: '4px 10px' }}>
                        {menus.map((m, i) => (
                            <MenuItem key={i} menu={m} />
                        ))}
                    </div>
                </nav>

                {/* ── Footer: Гарах ── */}
                <div
                    style={{
                        borderTop: `1px solid ${BORDER}`,
                        padding: collapsed ? '10px 8px' : '10px 10px',
                        flexShrink: 0,
                    }}
                >
                    {collapsed ? (
                        <Tooltip title="Гарах" placement="right" arrow>
                            <button
                                onClick={handleLogout}
                                style={{
                                    all: 'unset',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    padding: '9px 0',
                                    cursor: 'pointer',
                                    borderRadius: 8,
                                    transition: 'background .15s',
                                    color: DANGER,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = DANGER_HOVER_BG;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <LogOut size={15} strokeWidth={1.8} />
                            </button>
                        </Tooltip>
                    ) : (
                        <button
                            onClick={handleLogout}
                            style={{
                                all: 'unset',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '9px 12px',
                                cursor: 'pointer',
                                borderRadius: 8,
                                transition: 'background .15s',
                                boxSizing: 'border-box',
                                width: '100%',
                                color: DANGER,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = DANGER_HOVER_BG;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <span
                                style={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <LogOut size={15} strokeWidth={1.8} />
                            </span>
                            <span
                                style={{
                                    fontSize: 13.5,
                                    fontWeight: 500,
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                Гарах
                            </span>
                        </button>
                    )}
                </div>
            </aside>

            {/* ══════════ MAIN CONTENT ══════════ */}
            <main
                style={{
                    flex: 1,
                    minHeight: '100vh',
                    background: '#F9FAFB',
                    padding: '32px 40px',
                    overflowY: 'auto',
                }}
            >
                {children}
            </main>

            {system.showLoading && <LoadingDialog open={system.showLoading} />}
            {system.alert.isShow && (
                <CustomSnackBar
                    open={system.alert.isShow}
                    msg={system.alert.message}
                    handleClose={() =>
                        dispatch({
                            type: SET_ALERT,
                            data: { isShow: false, type: '', message: '' },
                        })
                    }
                    type={system.alert.type}
                />
            )}
        </div>
    );
};

export default AdminLayout;

AdminLayout.isAdminPage = true;
